// src/components/IssueListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import IssueCard from './IssueCard';
import CreateEntityModal from './CreateEntityModal';
import { ISSUE_STATUS_OPTIONS, ISSUE_SEVERITY_OPTIONS } from '../constants';
import { RiInformationLine as InformationCircleIcon, RiAddLine } from 'react-icons/ri';
import { usePersistedFilters, Tooltip, SingleSelectDropdown } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { ROUTES } from '../constants/routes';
import { useEntityNavigationContext } from './EntityNavigation';
import { useStaffContext } from '../contexts/StaffContext';

const IssueListView = () => {
    const { apiService, clientId, dateRange, setDateRange } = useOutletContext();
    const navigate = useNavigate();
    const { getDriDisplayName } = useStaffContext();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';

    const [issuesList, setIssuesList] = useState([]);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [errorIssues, setErrorIssues] = useState(null);

    // Use persisted filter for severity (can be set from dashboard card)
    const [issueSeverityFilter, setIssueSeverityFilter] = usePersistedFilters('issueSeverityFilter', '');
    const [issueStatusFilter, setIssueStatusFilter] = useState('');
    
    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = usePersistedFilters('selectedDris', []);
    
    // Create modal state
    const [showCreateIssueModal, setShowCreateIssueModal] = useState(false);
    const [initiativesList, setInitiativesList] = useState([]);


    const hasActiveFilters = selectedDris.length > 0 || issueSeverityFilter !== '' || issueStatusFilter !== '';
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
        setIssueSeverityFilter('');
        setIssueStatusFilter('');
    };

    const loadDris = useCallback(async () => {
        if (!apiService || !clientId) return;
        try {
            const staffResult = await apiService.getAllStaff(false);
            if (staffResult && staffResult.length > 0) {
                setDris(staffResult.map(s => s.staffName || s.name).filter(Boolean));
                return;
            }
            const driResult = await apiService.getClientDistinctDRIs(clientId);
            // Backend now returns normalized DRIs (staff names instead of emails)
            setDris(driResult.map(r => r.dri).filter(Boolean));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        }
    }, [apiService, clientId]);

    const loadIssuesList = useCallback(async () => {
        if (!apiService || !clientId || !dateRange || !dateRange.start || !dateRange.end) {
            setLoadingIssues(false);
            return;
        }
        setLoadingIssues(true); 
        setErrorIssues(null);
        
        try {
            const issues = await apiService.getAllIssuesList(
                clientId, 
                selectedDris.length > 0 ? selectedDris : null,
                issueSeverityFilter || null,
                issueStatusFilter || null,
                dateRange.start,
                dateRange.end
            );
            setIssuesList(issues);
        } catch (err) {
            setErrorIssues(err.message); 
            setIssuesList([]);
        } finally {
            setLoadingIssues(false);
        }
    }, [apiService, clientId, issueSeverityFilter, issueStatusFilter, selectedDris, dateRange]);

    useEffect(() => {
        if (clientId) {
            loadDris();
        }
    }, [clientId, loadDris]);

    useEffect(() => {
        if (clientId && dateRange && dateRange.start && dateRange.end) {
            loadIssuesList();
        }
    }, [loadIssuesList, clientId, dateRange, selectedDris]); 

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const handleIssueUpdate = (itemId, updatedField, newValue) => {
        setIssuesList(prevList => 
            prevList.map(item => 
                item.id === itemId ? { ...item, [updatedField]: newValue } : item
            )
        );
    };

    const handleIssueStatusChange = async (issueId, newStatus) => {
        try {
            await apiService.updateNodeStatus(issueId, newStatus);
            handleIssueUpdate(issueId, 'status', newStatus);
        } catch (err) {
            console.error('Error updating issue status:', err);
        }
    };

    const handleIssueSeverityChange = async (issueId, newSeverity) => {
        try {
            await apiService.updateNodeProperty(issueId, 'severity', newSeverity);
            handleIssueUpdate(issueId, 'severity', newSeverity);
        } catch (err) {
            console.error('Error updating issue severity:', err);
        }
    };

    // Load initiatives for the create modal
    const loadInitiativesList = useCallback(async () => {
        if (!apiService || !clientId || !dateRange?.start || !dateRange?.end) return;
        try {
            const initiatives = await apiService.getAllInitiativesList(
                clientId, null, null, dateRange.start, dateRange.end
            );
            setInitiativesList(initiatives);
        } catch (err) {
            console.error('Error loading initiatives:', err);
        }
    }, [apiService, clientId, dateRange]);

    useEffect(() => {
        if (clientId && dateRange?.start && dateRange?.end) {
            loadInitiativesList();
        }
    }, [clientId, dateRange, loadInitiativesList]);

    // Handle successful issue creation
    const handleIssueCreated = (newIssue) => {
        if (!newIssue) {
            loadIssuesList();
            return;
        }

        const issueDri = newIssue.assigned_to || newIssue.assignedTo || newIssue.dri || '';
        const matchesSeverity = !issueSeverityFilter || newIssue.severity === issueSeverityFilter;
        const matchesStatus = !issueStatusFilter || newIssue.status === issueStatusFilter;
        const matchesDri = selectedDris.length === 0 || (issueDri && selectedDris.some(dri => issueDri.includes(dri)));

        if (matchesSeverity && matchesStatus && matchesDri) {
            setIssuesList(prev => [newIssue, ...prev]);
        } else {
            // Clear filters so the newly created issue is visible
            setSelectedDris([]);
            setIssueSeverityFilter('');
            setIssueStatusFilter('');
            loadIssuesList();
        }
    };


    return (
        <>
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                dateRange={dateRange} onDateRangeChange={setDateRange}
                dris={dris} selectedDris={selectedDris} onDriChange={handleDriChange} driDisplayFormatter={getDriDisplayName}
                filterGroups={[{ id: 'issues', label: 'Issues', filters: [{ key: 'severity' }, { key: 'issueStatus' }] }]}
                filters={{ severity: issueSeverityFilter, issueStatus: issueStatusFilter }}
                filterConfigs={{
                    severity: { label: 'Severity', render: () => <SingleSelectDropdown id="severityFilter" label="Severity" value={issueSeverityFilter} onChange={(val) => setIssueSeverityFilter(val)} options={ISSUE_SEVERITY_OPTIONS} allLabel="All" /> },
                    issueStatus: { label: 'Issue Status', render: () => <SingleSelectDropdown id="statusFilter" label="Issue Status" value={issueStatusFilter} onChange={(val) => setIssueStatusFilter(val)} options={ISSUE_STATUS_OPTIONS} allLabel="All" /> }
                }}
                activeFilterChips={[
                    ...(issueSeverityFilter ? [{ label: 'Severity', value: issueSeverityFilter.charAt(0).toUpperCase() + issueSeverityFilter.slice(1), onRemove: () => setIssueSeverityFilter('') }] : []),
                    ...(issueStatusFilter ? [{ label: 'Status', value: issueStatusFilter.charAt(0).toUpperCase() + issueStatusFilter.slice(1), onRemove: () => setIssueStatusFilter('') }] : []),
                ]}
                onClearAll={handleClearAllFilters} hasActiveFilters={hasActiveFilters}
                variant={filterVariant} isExpanded={filtersExpanded}
            />
            
            {/* Issues Card Grid */}
            <div className="rounded-xl shadow p-4 md:p-6" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }} data-testid="issue-list">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>Issues</h3>
                        <Tooltip content="Issues include newly created items without communications, plus items mentioned in communications or blocking initiatives with communications in the selected date range.">
                            <InformationCircleIcon className="w-4 h-4 cursor-help" style={{ color: 'var(--pallium-icon-gray)' }} />
                        </Tooltip>
                    </div>
                    <button
                        onClick={() => setShowCreateIssueModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                        style={{ 
                            backgroundColor: 'rgba(252, 57, 57, 0.1)',
                            color: 'var(--pallium-error-red)',
                        }}
                    >
                        <RiAddLine className="w-4 h-4" />
                        New Issue
                    </button>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                    Issues mentioned in communications, plus newly created items without communications
                </p>

                {loadingIssues ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>Loading issues...</div>
                ) : errorIssues ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-error-red)' }}>Error loading issues: {errorIssues}</div>
                ) : issuesList.length === 0 ? (
                    <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(0, 156, 220, 0.1)', border: '1px solid var(--pallium-info-cyan)' }}>
                        <div className="flex items-start">
                            <InformationCircleIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: 'var(--pallium-info-cyan)' }} />
                            <div className="text-sm" style={{ color: 'var(--pallium-text-primary)' }}>
                                <strong>No issues found.</strong> This list includes newly created issues without communications, plus issues mentioned in communications or blocking initiatives in the selected date range ({dateRange?.start} to {dateRange?.end}).
                                {hasActiveFilters && (
                                    <>
                                        {' '}Your current filters may be hiding results.
                                        <button
                                            onClick={handleClearAllFilters}
                                            className="ml-2 underline"
                                            style={{ color: 'var(--pallium-primary-purple)' }}
                                        >
                                            Clear filters
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {issuesList.map((issue, index) => (
                            <IssueCard
                                key={issue.id || index}
                                data-testid="issue-card"
                                issue={{
                                    ...issue,
                                    issueName: issue.name,
                                    issueId: issue.id,
                                    dri: issue.assigned_to,
                                    blockedInitiatives: Array.isArray(issue.blockedInitiatives) ? issue.blockedInitiatives.length : issue.blockedInitiatives
                                }}
                                clientId={clientId}
                                onClick={() => navigate(`${ROUTES.ACCOUNTS}/${clientId}/issues/${issue.id}`)}
                                editable={true}
                                statusOptions={ISSUE_STATUS_OPTIONS}
                                severityOptions={ISSUE_SEVERITY_OPTIONS}
                                onStatusChange={handleIssueStatusChange}
                                onSeverityChange={handleIssueSeverityChange}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Create Issue Modal */}
            <CreateEntityModal
                isOpen={showCreateIssueModal}
                onClose={() => setShowCreateIssueModal(false)}
                entityType="issue"
                clientId={clientId}
                apiService={apiService}
                onSuccess={handleIssueCreated}
                staffList={dris}
                initiativesList={initiativesList}
            />
        </>
    );
};

export default IssueListView;